import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./BranchWallet.css";
import axios from "axios";
import { toast } from "react-toastify";
import { FileSpreadsheet, FileText } from "lucide-react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const API_URL = "https://payyug.in/api";

// Helper function to safely fetch image and convert to Base64
const getBase64ImageFromUrl = async (imageUrl) => {
  if (!imageUrl) return null;
  try {
    const response = await fetch(imageUrl, { mode: "cors" });
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Image loading failed:", error);
    return null;
  }
};

const BranchWallet = () => {
  // Read superAdminId safely
  const superAdminId = useMemo(
    () => Number(localStorage.getItem("adminuserId") || 14),
    []
  );

  // 1. Form & Company State
  const [formData, setFormData] = useState({
    branchId: "",
    cashBalance: "",
  });

  const [branches, setBranches] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [company, setCompany] = useState(null);

  // 2. Pagination State
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  /* ================= FETCH COMPANY DETAILS ================= */
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await fetch(
          `${API_URL}/get-company?superAdminId=${superAdminId}`
        );
        const data = await response.json();

        if (response.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          setCompany(data);
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
      }
    };

    if (superAdminId) fetchCompany();
  }, [superAdminId]);

  /* ================= FETCH BRANCHES ================= */
  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const res = await axios.get(`${API_URL}/get-branch`, {
          params: {
            SuperAdminId: superAdminId,
            limit: "all",
          },
        });
        setBranches(res?.data?.branches || []);
      } catch (err) {
        console.error("Fetch Branch Error:", err);
        toast.error("Failed to load branches");
      }
    };

    fetchBranch();
  }, [superAdminId]);

  /* ================= FETCH WALLET REPORTS ================= */
  const fetchBranchWallet = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/list/branch-wallet-master`, {
        params: {
          superAdminId,
          page: currentPage,
          limit: rowsPerPage === "ALL" ? 100000 : rowsPerPage,
        },
      });

      setReportData(res?.data?.data || []);
      setTotalRecords(res?.data?.totalRecords || res?.data?.data?.length || 0);
    } catch (error) {
      console.error("Fetch Wallet Error:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Error fetching report data."
      );
    } finally {
      setLoading(false);
    }
  }, [superAdminId, currentPage, rowsPerPage]);

  useEffect(() => {
    fetchBranchWallet();
  }, [fetchBranchWallet]);

  /* ================= FORM HANDLERS ================= */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.branchId) {
      toast.warn("Please select a branch");
      return;
    }

    const payload = {
      cashBalance: Number(formData.cashBalance),
      bankBalance: 0,
      superAdminId: superAdminId,
      branchId: Number(formData.branchId),
      status: "ACTIVE",
    };

    try {
      const res = await axios.post(
        `${API_URL}/create/branch-wallet-master`,
        payload
      );

      if (res?.data?.success || res?.status === 200 || res?.status === 201) {
        toast.success(res?.data?.message || "Branch Wallet Created.");

        // Reset Form
        setFormData({
          branchId: "",
          cashBalance: "",
        });

        // Refresh Table Data
        fetchBranchWallet();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Branch Wallet Creation Error"
      );
    }
  };

  /* ================= SEARCH & FILTERING ================= */
  const filteredData = useMemo(() => {
    let data = [...reportData];

    if (searchQuery.trim()) {
      data = data.filter((item) => {
        const branchMatch = String(
          item.branchId || item.branchName || ""
        ).toLowerCase().includes(searchQuery.toLowerCase());
        const statusMatch = String(item.status || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        return branchMatch || statusMatch;
      });
    }

    return data;
  }, [reportData, searchQuery]);

  /* ================= PAGINATION COMPUTATIONS ================= */
  const totalEntries = totalRecords || filteredData?.length || 0;
  const isShowAll = rowsPerPage === "ALL";
  const effectiveRowsPerPage = isShowAll ? totalEntries : Number(rowsPerPage);
  const totalPages =
    isShowAll || totalEntries === 0
      ? 1
      : Math.ceil(totalEntries / effectiveRowsPerPage);

  const startIndex = isShowAll ? 0 : (currentPage - 1) * effectiveRowsPerPage;
  const endIndex = isShowAll
    ? totalEntries
    : Math.min(startIndex + effectiveRowsPerPage, totalEntries);

  const currentTableData =
    filteredData.length <= effectiveRowsPerPage || isShowAll
      ? filteredData
      : filteredData.slice(startIndex, endIndex);

  /* ================= EXPORT TO EXCEL ================= */
  const exportToExcel = () => {
    if (filteredData.length === 0) {
      toast.error("No data available to export");
      return;
    }

    const exportList = filteredData.map((item, index) => ({
      SN: index + 1,
      "Branch ID": item.branchId || item.branchName || "N/A",
      "Cash Balance": item.cashBalance ?? 0,
      Status: item.status || "ACTIVE",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportList);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Branch Wallet");
    XLSX.writeFile(workbook, "Branch_Wallet_Report.xlsx");
  };

  /* ================= EXPORT TO PDF ================= */
  const exportToPDF = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error("No data available to export");
      return;
    }

    const doc = new jsPDF({
  orientation: "p",
  unit: "mm",
  format: "a4",
  compress: true,
});
    const pageWidth = doc.internal.pageSize.width;

    // ================= LOGO SECTION (SQUARE, NO BORDER) =================
    const logoX = 15;
    const logoY = 12;
    const logoSize = 22;

    if (company?.logoUrl) {
      const base64Logo = await getBase64ImageFromUrl(company.logoUrl);
      if (base64Logo) {
        try {
          const isJpeg =
            company.logoUrl.toLowerCase().includes(".jpg") ||
            company.logoUrl.toLowerCase().includes(".jpeg");
          const format = isJpeg ? "JPEG" : "PNG";

          doc.addImage(base64Logo, format, logoX, logoY, logoSize, logoSize);
        } catch (err) {
          console.warn("Logo draw error:", err);
        }
      }
    }

    // ================= COMPANY DETAILS =================
    const nameX = logoX + logoSize + 8;
    const nameY = 16;
    const nameMaxWidth = pageWidth - nameX - 15;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(0, 59, 70);

    const companyNameText = doc.splitTextToSize(
      (company?.name || "COMPANY NAME").toUpperCase(),
      nameMaxWidth
    );
    doc.text(companyNameText, nameX, nameY);

    let currentY = nameY + companyNameText.length * 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);

    const addressText = doc.splitTextToSize(
      company?.address || "Company Address",
      nameMaxWidth
    );
    doc.text(addressText, nameX, currentY);
    currentY += addressText.length * 4;

    doc.text(`Email: ${company?.email || "-"}`, nameX, currentY);
    currentY += 4;

    doc.text(`Phone: ${company?.contact || "-"}`, nameX, currentY);
    currentY += 10;

    // Divider Line
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(10, currentY, pageWidth - 10, currentY);

    // ================= REPORT TITLE =================
    currentY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Branch Wallet Report", pageWidth / 2, currentY, {
      align: "center",
    });

    // ================= TABLE DATA =================
    const tableColumn = [
      "SN",
      "Branch ID",
      "Cash Balance",
      "Status",
    ];
    const tableRows = filteredData.map((item, index) => [
      index + 1,
      item.branchId || item.branchName || "N/A",
      `Rs. ${item.cashBalance ?? 0}`,
      item.status || "ACTIVE",
    ]);

    autoTable(doc, {
      startY: currentY + 6,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        halign: "center",
        valign: "middle",
        textColor: [50, 50, 50],
      },
      headStyles: {
        fillColor: [0, 59, 70],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8.5,
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      margin: { left: 10, right: 10, top: 10, bottom: 15 },

      didDrawPage: function (data) {
        const pWidth = doc.internal.pageSize.width;
        const pHeight = doc.internal.pageSize.height;

        // Page Border
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.2);
        doc.rect(8, 8, pWidth - 16, pHeight - 16);

        // Footer Page Numbering
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
          pWidth / 2,
          pHeight - 11,
          { align: "center" }
        );
      },
    });

    doc.save("Branch_Wallet_Report.pdf");
  };

  return (
    <div className="bw-container">
      {/* --- BRANCH WALLET FORM SECTION --- */}
      <div className="bw-card-header">
        <h2 className="bw-title">Branch Wallet</h2>
        <form onSubmit={handleFormSubmit}>
          <div className="bw-form-grid">
            {/* Branch Dropdown */}
            <div className="bw-field-group">
              <label className="bw-label">Select Branch</label>
              <select
                name="branchId"
                value={formData.branchId}
                onChange={handleInputChange}
                required
                className="bw-select"
              >
                <option value="">-- Select Branch --</option>
                {branches.map((b) => (
                  <option key={b.id || b._id} value={b.id || b._id}>
                    {b.name || b.branchName || `Branch ID: ${b.id || b._id}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Cash Balance */}
            <div className="bw-field-group">
              <label className="bw-label">Cash Balance</label>
              <input
                type="number"
                name="cashBalance"
                placeholder="Enter Cash Balance"
                value={formData.cashBalance}
                onChange={handleInputChange}
                required
                className="bw-input"
              />
            </div>
          </div>

          <div className="bw-submit-bar">
            <button type="submit" className="bw-btn-submit">
              Save Branch Wallet
            </button>
          </div>
        </form>
      </div>

      {/* --- REPORT TABLE CONTROLS SECTION --- */}
      <div className="bw-table-controls">
        <div className="bw-entries-wrapper">
          <span>Show</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(
                e.target.value === "ALL" ? "ALL" : Number(e.target.value)
              );
              setCurrentPage(1);
            }}
            className="bw-entries-select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="ALL">All</option>
          </select>
          <span>entries</span>
        </div>

        <input
          type="text"
          className="bw-search-input"
          placeholder="Search by Branch / Status..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />

        <div className="bw-export-btns">
          <button
            type="button"
            className="bw-export-btn bw-excel-btn"
            onClick={exportToExcel}
            title="Export to Excel"
          >
            <FileSpreadsheet size={16} style={{ marginRight: "4px" }} /> Excel
          </button>

          <button
            type="button"
            className="bw-export-btn bw-pdf-btn"
            onClick={exportToPDF}
            title="Export to PDF"
          >
            <FileText size={16} style={{ marginRight: "4px" }} /> PDF
          </button>
        </div>
      </div>

      {/* --- REPORT TABLE SECTION --- */}
      <div className="bw-table-wrapper">
        <table className="bw-report-table">
          <thead>
            <tr>
              <th>SN</th>
              <th>Branch ID</th>
              <th>Cash Balance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                  Loading Data...
                </td>
              </tr>
            ) : currentTableData.length > 0 ? (
              currentTableData.map((row, index) => {

                return (
                  <tr key={row.id || row._id || index}>
                    <td>{startIndex + index + 1}</td>
                    <td>{row.branchId || row.branchName || "N/A"}</td>
                    <td>₹{row.cashBalance ?? 0}</td>
                    <td>
                      <span
                        className={
                          row.status === "ACTIVE"
                            ? "bw-badge-active"
                            : "bw-badge-inactive"
                        }
                      >
                        {row.status || "ACTIVE"}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="5"
                  style={{ textAlign: "center", color: "#777", padding: "20px" }}
                >
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- PAGINATION BAR --- */}
      {totalEntries > 0 && (
        <div className="bw-pagination-bar">
          <span className="bw-pagination-info">
            Showing {startIndex + 1} to {endIndex} of {totalEntries} entries
          </span>
          {!isShowAll && totalPages > 1 && (
            <div className="bw-pagination-btns">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className="bw-page-btn"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`bw-page-btn ${
                      currentPage === page ? "active" : ""
                    }`}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="bw-page-btn"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BranchWallet;